import React, { useEffect, useRef } from 'react';
import { FiTarget, FiUsers, FiZap, FiHeart } from 'react-icons/fi';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import useGSAPScrollAnimations from '../hooks/useGSAPScrollAnimations';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const About = () => {
  const sectionRef = useRef(null);
  const heroRef = useRef(null);
  const missionRef = useRef(null);
  const foundersRef = useRef(null);
  const valuesRef = useRef(null);
  
  const { fadeInUp, fadeInLeft, fadeInRight, scaleIn, staggerIn, parallax } = useGSAPScrollAnimations();

  useEffect(() => {
    // Hero section animation
    if (heroRef.current) {
      const heroElements = heroRef.current.children;
      staggerIn(Array.from(heroElements), { 
        stagger: 0.2,
        y: 60,
        delay: 0.3 
      });
    }
    
    // Mission section animation
    if (missionRef.current) {
      scaleIn(missionRef.current, { delay: 0.2 });
    }
    
    // Founders section animation
    if (foundersRef.current) {
      const founderCards = foundersRef.current.querySelectorAll('.founder-card');
      Array.from(founderCards).forEach((card, index) => {
        gsap.fromTo(card,
          {
            y: 80,
            opacity: 0,
            rotationY: 15
          },
          {
            y: 0,
            opacity: 1,
            rotationY: 0,
            duration: 1,
            ease: "power2.out",
            scrollTrigger: {
              trigger: card,
              start: "top 85%",
              end: "bottom 15%",
              toggleActions: "play none none reverse"
            },
            delay: index * 0.2
          }
        );
      });
    }
    
    // Values section animation
    if (valuesRef.current) {
      const valueCards = valuesRef.current.children;
      staggerIn(Array.from(valueCards), { 
        stagger: 0.15,
        y: 50,
        delay: 0.3 
      });
    }

    // Parallax background elements
    const backgroundElements = sectionRef.current?.querySelectorAll('.parallax-bg');
    if (backgroundElements) {
      backgroundElements.forEach((element, index) => {
        parallax(element, { 
          yPercent: index % 2 === 0 ? -30 : 30,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: 1
          }
        });
      });
    }

    // Floating elements animation
    const floatingElements = sectionRef.current?.querySelectorAll('.floating-element');
    if (floatingElements) {
      floatingElements.forEach((element, index) => {
        gsap.to(element, {
          y: index % 2 === 0 ? -20 : 20,
          rotation: index % 2 === 0 ? 5 : -5,
          duration: 3 + index,
          repeat: -1,
          yoyo: true,
          ease: "power2.inOut",
          delay: index * 0.5
        });
      });
    }
  }, [fadeInUp, fadeInLeft, fadeInRight, scaleIn, staggerIn, parallax]);

  const founders = [
    {
      name: "Beryl Gabriel",
      role: "Founder",
      bio: "Visionary leader passionate about democratizing AI access and creating seamless user experiences.",
      image: "/api/placeholder/300/300",
      gradient: "from-secondary-500 to-accent-500"
    },
    {
      name: "Jivin Michaeswaran",
      role: "Founder",
      bio: "Technical innovator dedicated to building scalable AI infrastructure and cutting-edge solutions.",
      image: "/api/placeholder/300/300",
      gradient: "from-accent-500 to-primary-500"
    }
  ];

  const values = [
    {
      icon: FiTarget,
      title: "Our Mission",
      description: "To bring all AI tools and technologies to one place, making them easily accessible for everyone.",
      gradient: "from-secondary-500 to-secondary-600"
    },
    {
      icon: FiUsers,
      title: "User-Centric",
      description: "We prioritize user experience and accessibility, ensuring our platform serves everyone effectively.",
      gradient: "from-accent-500 to-accent-600"
    },
    {
      icon: FiZap,
      title: "Innovation",
      description: "Constantly pushing boundaries to deliver cutting-edge AI solutions and features.",
      gradient: "from-primary-500 to-primary-600"
    },
    {
      icon: FiHeart,
      title: "Community",
      description: "Building a thriving community of AI enthusiasts, developers, and innovators.",
      gradient: "from-cyan-500 to-blue-500"
    }
  ];

  return (
    <section 
      ref={sectionRef}
      id="about" 
      className="py-24 px-6 relative overflow-hidden min-h-screen"
    >
      {/* Background Elements with Parallax */}
      <div className="absolute inset-0 opacity-30">
        <div className="parallax-bg absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-secondary-500/20 to-accent-500/20 rounded-full blur-3xl"></div>
        <div className="parallax-bg absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-to-r from-accent-500/20 to-primary-500/20 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto relative z-10">
        {/* Hero Section */}
        <div ref={heroRef} className="text-center mb-20">
          <h1 className="text-5xl md:text-7xl font-display font-bold mb-6">
            <span className="gradient-text">About Bervin</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            We're on a mission to revolutionize how people interact with AI by bringing all tools and technologies together in one unified, accessible platform.
          </p>
        </div>

        {/* Mission Statement */}
        <div 
          ref={missionRef}
          className="mb-20 p-12 glass border border-white/10 rounded-3xl text-center"
        >
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-6">
              <span className="gradient-text">Our Goal</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed mb-8">
              To bring all AI tools and technologies to one place, making them easily accessible for everyone. 
              We believe that powerful AI capabilities shouldn't be scattered across dozens of platforms, 
              but unified in a single, intuitive experience that empowers users to achieve more.
            </p>
            <div className="flex justify-center">
              <div className="w-24 h-1 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-full"></div>
            </div>
          </div>
        </div>

        {/* Founders Section */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-6">
              <span className="gradient-text">Meet Our Founders</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              The visionary minds behind Bervin, dedicated to making AI accessible to everyone.
            </p>
          </div>

          <div ref={foundersRef} className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {founders.map((founder, index) => (
              <div
                key={founder.name}
                className="founder-card group relative"
              >
                {/* Card */}
                <div className="relative h-full p-8 glass border border-white/10 rounded-3xl overflow-hidden hover-lift">
                  {/* Background Gradient */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${founder.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>
                  
                  {/* Profile Image */}
                  <div className="relative mb-6 flex justify-center">
                    <div className={`relative w-32 h-32 rounded-full overflow-hidden border-4 border-transparent bg-gradient-to-r ${founder.gradient} p-1`}>
                      <div className="w-full h-full rounded-full bg-dark-800 flex items-center justify-center">
                        <div className={`w-24 h-24 rounded-full bg-gradient-to-r ${founder.gradient} flex items-center justify-center text-white text-3xl font-bold`}>
                          {founder.name.split(' ').map(n => n[0]).join('')}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="relative z-10 text-center">
                    <h3 className="text-2xl font-bold text-white mb-2">
                      {founder.name}
                    </h3>
                    <p className={`text-lg font-medium mb-4 bg-gradient-to-r ${founder.gradient} bg-clip-text text-transparent`}>
                      {founder.role}
                    </p>
                    <p className="text-gray-300 leading-relaxed mb-6">
                      {founder.bio}
                    </p>
                  </div>

                  {/* Corner Accent */}
                  <div className="absolute top-0 right-0 w-20 h-20 overflow-hidden rounded-3xl">
                    <div className={`absolute -top-10 -right-10 w-20 h-20 bg-gradient-to-br ${founder.gradient} opacity-20 rotate-45`}></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Values Section */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-6">
              <span className="gradient-text">Our Values</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              The principles that guide everything we do at Bervin.
            </p>
          </div>

          <div ref={valuesRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={value.title}
                className="group relative"
              >
                {/* Card */}
                <div className="relative h-full p-8 glass border border-white/10 rounded-2xl overflow-hidden hover-lift">
                  {/* Background Gradient */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${value.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                  
                  {/* Icon Container */}
                  <div className={`relative w-16 h-16 bg-gradient-to-r ${value.gradient} rounded-xl flex items-center justify-center mb-6 shadow-lg`}>
                    <value.icon className="w-8 h-8 text-white" />
                    
                    {/* Glow Effect */}
                    <div className={`absolute inset-0 bg-gradient-to-r ${value.gradient} rounded-xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-300`}></div>
                  </div>

                  {/* Content */}
                  <div className="relative z-10">
                    <h3 className="text-xl font-bold text-white mb-4">
                      {value.title}
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>



        {/* Floating Elements */}
        <div
          className="floating-element absolute -top-8 -left-8 w-16 h-16 bg-gradient-to-r from-secondary-500/30 to-accent-500/30 rounded-xl blur-lg"
        />
        
        <div
          className="floating-element absolute -bottom-8 -right-8 w-20 h-20 bg-gradient-to-r from-accent-500/30 to-primary-500/30 rounded-full blur-lg"
        />
      </div>
    </section>
  );
};

export default About;
